import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from '../components/CheckoutForm';
import AnimatedBackground from '../components/AnimatedBackground';

const API_URL = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    fetchMod();
  }, [id]);

  const fetchMod = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mods/${id}`);
      setMod(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching mod:', error);
      setLoading(false);
    }
  };

  const handleBuyNow = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/create-payment-intent`, {
        modId: mod.id,
      });
      setClientSecret(response.data.clientSecret);
      setShowCheckout(true);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      alert('Failed to initialize payment. Please try again.');
    }
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-green-500 font-mono animate-pulse">Loading digital asset...</div>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen gap-4">
        <span className="material-symbols-outlined text-6xl text-gray-600">error</span>
        <div className="text-gray-400 font-mono">Asset not found</div>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-green-500 text-black font-bold rounded hover:bg-white transition-all"
        >
          Return to Marketplace
        </button>
      </div>
    );
  }

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#00ff41',
      colorBackground: '#0a110e',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'Space Grotesk, monospace',
      borderRadius: '8px',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="w-full relative">
      {/* Animated Background - Coding/Tech Theme */}
      <AnimatedBackground
        imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
        opacity={0.5}
      />

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 md:px-8 py-6">
        {/* Breadcrumbs */}
      <div className="flex flex-wrap gap-2 py-4 mb-2 text-sm font-mono">
        <a className="text-gray-400 hover:text-green-500 transition-colors cursor-pointer" onClick={() => navigate('/')}>
          [ Home ]
        </a>
        <span className="text-gray-600">/</span>
        <span className="text-green-500">{mod.name}</span>
      </div>

      {/* Header Info */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8 pb-6 border-b border-white/10">
        <div className="flex flex-col gap-2">
          <div className="inline-flex items-center gap-2 mb-2">
            <span className="text-blue-400 font-mono text-xs tracking-widest uppercase">[ DIGITAL ASSET ]</span>
          </div>
          <h1 className="text-white text-3xl md:text-5xl font-black leading-tight tracking-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {mod.name}
          </h1>
          <p className="text-gray-500 font-mono text-sm">&gt; By {mod.author}</p>
        </div>
        <div className="flex items-center gap-1 bg-[#0a110e] p-3 rounded border border-white/10">
          <span className="text-yellow-500 text-2xl">★</span>
          <span className="text-white font-bold ml-1">{mod.rating}</span>
          <span className="text-gray-400 text-sm ml-1 font-mono">({mod.reviews})</span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Media & Details */}
        <div className="lg:col-span-2 flex flex-col gap-8">
          {/* Media Gallery */}
          <div className="flex flex-col gap-4">
            <div className="w-full aspect-video bg-black overflow-hidden border border-blue-400/20 relative group"
              style={{clipPath: 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)'}}>
              <img
                alt={mod.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                src={mod.image}
              />
              <div className="absolute top-3 right-3 bg-green-500 text-black px-3 py-1 text-xs font-bold uppercase tracking-wider">
                {mod.category}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-6 text-gray-400 leading-relaxed font-mono text-sm">
            <div className="border-l-2 border-green-500 pl-4">
              <p className="mb-4">&gt; {mod.description}</p>
              <p>&gt; This high-quality mod is perfect for enhancing your FiveM server with premium content. Installation is straightforward and compatible with most frameworks.</p>
            </div>

            <h3 className="text-white text-xl font-bold mt-4 flex items-center gap-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              <span className="material-symbols-outlined text-green-500">terminal</span>
              KEY_FEATURES
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <li className="flex items-start gap-3 bg-[#0a110e] p-4 border border-white/10 hover:border-green-500/50 transition-all">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Fully optimized performance</span>
              </li>
              <li className="flex items-start gap-3 bg-[#0a110e] p-4 border border-white/10 hover:border-green-500/50 transition-all">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Easy installation process</span>
              </li>
              <li className="flex items-start gap-3 bg-[#0a110e] p-4 border border-white/10 hover:border-green-500/50 transition-all">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Regular updates included</span>
              </li>
              <li className="flex items-start gap-3 bg-[#0a110e] p-4 border border-white/10 hover:border-green-500/50 transition-all">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>24/7 support available</span>
              </li>
            </ul>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-4">
              {mod.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white/5 border border-white/10 text-gray-400 text-xs uppercase tracking-wider hover:border-green-500/50 transition-all"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Sticky Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 flex flex-col gap-6">
            {/* Purchase Terminal */}
            <div className="bg-[#0a110e] border border-blue-400/20 p-6 shadow-xl relative overflow-hidden holo-card"
              style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)'}}>
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-green-500/10 blur-[50px] rounded-full pointer-events-none"></div>

              <div className="flex justify-between items-baseline mb-2 relative z-10">
                <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">[ License Type ]</span>
              </div>

              <div className="flex items-baseline gap-2 mb-2 relative z-10">
                <span className="text-4xl font-black text-white tracking-tighter" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{formatPrice(mod.price)}</span>
                <span className="text-xs text-blue-400 font-mono ml-auto">USD</span>
              </div>

              <p className="text-gray-500 text-xs font-mono mb-6 relative z-10">&gt; Single Server License</p>

              {!showCheckout ? (
                <div className="flex flex-col gap-3 relative z-10">
                  <button
                    onClick={handleBuyNow}
                    className="flex items-center justify-center gap-2 w-full h-12 bg-green-500 hover:bg-white text-black font-bold uppercase tracking-widest transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:shadow-[0_0_25px_#00ff41]"
                  >
                    <span className="material-symbols-outlined text-[20px]">shopping_cart_checkout</span>
                    Purchase Now
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full h-10 border border-white/20 text-white font-mono hover:bg-white/5 transition-all uppercase text-sm"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="relative z-10">
                  {clientSecret && (
                    <Elements options={options} stripe={stripePromise}>
                      <CheckoutForm modId={mod.id} />
                    </Elements>
                  )}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-3 relative z-10">
                <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                  <span className="text-green-500">✓</span>
                  <span>Instant Download & Secure Payment</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                  <span className="text-green-500">✓</span>
                  <span>Virus Scanned & Secure Asset</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
                  <span className="text-green-500">✓</span>
                  <span>Powered by Stripe Payments</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

export default ProductDetailPage;
